// documentSetSlice.ts
// Redux Toolkit slice for set-based document fetching (personal / shared tabs).
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getDocumentsBySet, DocumentLight, SetType } from '../../services/document-set-service';
import { deleteDocument } from '../api/documents/documentsThunks';

// ── State ─────────────────────────────────────────────────────────────────────

export interface DocumentSetState {
    currentSet: SetType;
    documents: DocumentLight[];
    loading: boolean;
    error: string | null;
    totalCount: number;
    page: number;
    pageSize: number;
    ownerId: string | null;
    search: string;
}

const initialState: DocumentSetState = {
    currentSet: 'shared',
    documents: [],
    loading: false,
    error: null,
    totalCount: 0,
    page: 1,
    pageSize: 20,
    ownerId: null,
    search: '',
};

// ── Thunks ────────────────────────────────────────────────────────────────────

export interface FetchDocumentsBySetParams {
    setType: SetType;
    page?: number;
    pageSize?: number;
    search?: string;
    documentTypeId?: string;
    ownerId?: string;
}

export const fetchDocumentsBySet = createAsyncThunk<
    { documents: DocumentLight[]; totalCount: number; page: number; setType: SetType },
    FetchDocumentsBySetParams,
    { rejectValue: string }
>(
    'documentSet/fetchDocuments',
    async (params, { rejectWithValue }) => {
        try {
            const response = await getDocumentsBySet(params);
            return {
                documents: response.items,
                totalCount: response.totalCount,
                page: response.page,
                setType: params.setType,
            };
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message ?? err?.message ?? 'Ошибка загрузки документов'
            );
        }
    }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const documentSetSlice = createSlice({
    name: 'documentSet',
    initialState,
    reducers: {
        setCurrentSet(state, action: PayloadAction<SetType>) {
            state.currentSet = action.payload;
        },
        setOwnerId(state, action: PayloadAction<string | null>) {
            state.ownerId = action.payload;
        },
        setSearch(state, action: PayloadAction<string>) {
            state.search = action.payload;
        },
        clearDocuments(state) {
            state.documents = [];
            state.totalCount = 0;
            state.page = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDocumentsBySet.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDocumentsBySet.fulfilled, (state, action) => {
                state.loading = false;
                state.documents = action.payload.documents;
                state.totalCount = action.payload.totalCount;
                state.page = action.payload.page;
                state.currentSet = action.payload.setType;
            })
            .addCase(fetchDocumentsBySet.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Ошибка загрузки';
            })
            .addCase(deleteDocument.fulfilled, (state, action: PayloadAction<string>) => {
                state.documents = state.documents.filter(d => d.id !== action.payload);
                state.totalCount = Math.max(0, state.totalCount - 1);
            });
    },
});

export const { setCurrentSet, setOwnerId, setSearch, clearDocuments } = documentSetSlice.actions;
export default documentSetSlice.reducer;
